import React, { useState } from 'react'
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import StyledText from '../StyledComponents/StyledText';

type ValidationType = 'generic' | 'image' | 'video' | 'facebook' | 'linkedin' | 'youtube' | 'twitter' | 'subdomain' | 'domain-or-name';

interface UrlInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  name?: string;
  hasError?: string;
  FixedPart?: string | React.ReactNode;
  FixedPartWidth?: string;
  validationType?: ValidationType;
  validateOnChange?: boolean;
  showValidationStatus?: boolean;
  disabled?: boolean;
}

// Video platform URL patterns
const VIDEO_PATTERNS = {
  youtube: /^(?:(?:www\.)?youtube\.com\/watch\?v=[\w-]+|youtu\.be\/[\w-]+)$/,
  youtubeShorts: /^(?:www\.)?youtube\.com\/shorts\/[\w-]+$/,
  vimeo: /^(?:www\.)?vimeo\.com\/\d+/,
  dailymotion: /^(?:www\.)?dailymotion\.com\/video\//,
  youtubeEmbed: /^(?:www\.)?youtube\.com\/embed\//,
  fileExtensions: /\.(mp4|webm|ogg|mov|avi|mkv|m4v|3gp|m3u8)(?:\?.*)?$/i,
};

// Image file extensions and domains
const IMAGE_PATTERNS = {
  fileExtensions: /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff)(?:\?.*)?$/i,
  imageDomains: /^(?:imgur\.com|imgbb\.com|cloudinary\.com|imgix\.net|cdn\.jsdelivr\.net|cdnjs\.cloudflare\.com|images\.unsplash\.com|images\.pexels\.com|pixabay\.com|freepik\.com|img\.freepik\.com|via\.placeholder\.com|i\.pinimg\.com|cdn\.shopify\.com|images\.ctfassets\.net)/i,
  dataUrl: /^data:image\//i,
};

// Social media URL patterns
const SOCIAL_MEDIA_PATTERNS = {
  facebook: /^(?:facebook\.com|m\.facebook\.com)/i,
  linkedin: /^linkedin\.com/i,
  youtube: /^(?:youtube\.com|youtu\.be|m\.youtube\.com)/i,
  twitter: /^(?:twitter\.com|x\.com)/i,
};

const isVideoUrl = (urlStr: string): boolean => {
  return Object.values(VIDEO_PATTERNS).some(pattern => pattern.test(urlStr));
};

const isImageUrl = (urlStr: string): boolean => {
  return IMAGE_PATTERNS.fileExtensions.test(urlStr) || IMAGE_PATTERNS.imageDomains.test(urlStr) || IMAGE_PATTERNS.dataUrl.test(urlStr);
};

const validateUrl = (urlStr: string, validationType: ValidationType): boolean => {
  if (!urlStr) return true; // Empty is valid

  // For image validation: reject video URLs, accept image URLs
  if (validationType === 'image') {
    if (isVideoUrl(urlStr)) return false; // Reject video URLs
    return isImageUrl(urlStr); // Accept only image URLs
  }

  // For video validation: reject image-only URLs, accept video URLs
  if (validationType === 'video') {
    // Allow known video platforms and direct video file links only
    return isVideoUrl(urlStr);
  }

  // Social media validation
  if (validationType === 'facebook') {
    return SOCIAL_MEDIA_PATTERNS.facebook.test(urlStr);
  }

  if (validationType === 'linkedin') {
    return SOCIAL_MEDIA_PATTERNS.linkedin.test(urlStr);
  }

  if (validationType === 'youtube') {
    return SOCIAL_MEDIA_PATTERNS.youtube.test(urlStr);
  }

  if (validationType === 'twitter') {
    return SOCIAL_MEDIA_PATTERNS.twitter.test(urlStr);
  }

  // Generic validation: only check for valid domain format
  if (validationType === 'generic') {
    // Standard domain validation only - no video/image specific checks
    return /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/.*)?$/.test(urlStr);
  }

  if (validationType === 'subdomain') {
    return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(urlStr);
  }

  if (validationType === 'domain-or-name') {
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/.*)?$/;
    const nameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
    return domainRegex.test(urlStr) || nameRegex.test(urlStr);
  }

  return true;
};

const CustomInputURL: React.FC<UrlInputProps> = ({
  value,
  onChange,
  placeholder = 'example',
  className = '',
  name = 'url',
  hasError,
  FixedPart = "https://",
  FixedPartWidth = '85px',
  validationType = 'generic',
  validateOnChange = false,
  showValidationStatus = false,
  disabled = false
}) => {
  const { theme } = useTheme();

  const [isTouched, setIsTouched] = useState(false);

  // Reset isTouched when hasError is cleared
  React.useEffect(() => {
    if (!hasError) {
      setIsTouched(false);
    }
  }, [hasError]);

  // Extract domain part (after https://)
  const domainPart = value?.replace(/^https?:\/\//i, '');

  const isValidUrl = () => validateUrl(domainPart || '', validationType);

  const getErrorMessage = (): string => {
    if (validationType === 'image') {
      return 'Enter a valid image URL (e.g., image.jpg, imgur.com/...)';
    }
    if (validationType === 'video') {
      return 'Enter a valid video URL (e.g., youtube.com/watch?v=..., youtu.be/...)';
    }
    if (validationType === 'facebook') {
      return 'Enter a valid Facebook URL (facebook.com only)';
    }
    if (validationType === 'linkedin') {
      return 'Enter a valid LinkedIn URL (linkedin.com only)';
    }
    if (validationType === 'youtube') {
      return 'Enter a valid YouTube URL (youtube.com or youtu.be only)';
    }
    if (validationType === 'twitter') {
      return 'Enter a valid Twitter/X URL (twitter.com or x.com only)';
    }
    if (validationType === 'subdomain') {
      return 'Subdomain can only contain letters, numbers, and hyphens (e.g., "store" or "my-profile")';
    }
    if (validationType === 'domain-or-name') {
      return 'Enter a valid domain (e.g., "abc.f10.com") or a valid name (e.g., "mernitsolutions")';
    }
    return 'Enter a valid URL (e.g., www.example.com)';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    // If empty, send empty string
    if (!newValue || newValue.trim() === '') {
      if (validateOnChange) {
        setIsTouched(false);
      }
      onChange('');
      return;
    }

    // For YouTube URLs, don't prepend https:// if already starting with a protocol
    if (newValue.includes('youtube.com') || newValue.includes('youtu.be')) {
      if (!newValue.startsWith('https://') && !newValue.startsWith('http://')) {
        newValue = 'https://' + newValue;
      }
    } else {
      // Always prepend https:// for regular domains if not already there
      if (!newValue.startsWith('https://') && !newValue.startsWith('http://')) {
        newValue = 'https://' + newValue;
      }
    }

    if (validateOnChange) {
      setIsTouched(true);
    }

    onChange(newValue);
  };

  const handleBlur = () => {
    setIsTouched(true);
  };

  // Only show error if touched AND has value AND invalid
  const showError = isTouched && domainPart && !isValidUrl();
  const showValid = showValidationStatus && isTouched && domainPart && isValidUrl();

  return (
    <div className={`position-relative ${className}`}>
      <InputContainer FixedPartWidth={FixedPartWidth} className="flex items-center overflow-hidden" style={{ borderColor: hasError ? theme.CreateFormTheme.colors.error : theme.CreateFormTheme.colors.inputBorders, background: theme.CreateFormTheme.colors.inputBackground, opacity: disabled ? 0.6 : 1 }}>
        {/* Fixed "https://" part */}
        <div
          className="urlCon"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.CorporateTheme.colors.headerBG,
            color: theme.CorporateTheme.colors.headerColor,
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: '"Raleway", sans-serif',
            padding: '11px 14px',
            height: '40px',
            width: FixedPartWidth,
          }}
        >
          {FixedPart}
        </div>
        {/* Input field for domain */}
        <input
          type="text"
          name={name}
          value={domainPart}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`position-absolute ${showError ? 'border-red-500' : ''
            }`}
          style={{ borderColor: theme.CreateFormTheme.colors.inputBorders, backgroundColor: theme.CreateFormTheme.colors.inputBackground, color: theme.CorporateTheme.colors.headerColor, cursor: disabled ? 'not-allowed' : 'text' }}
        />
      </InputContainer>

      {showError && (
        <div
          style={{ fontSize: 12, color: theme.CreateFormTheme.colors.error }}
        >
          {hasError || getErrorMessage()}
        </div>
      )}
      {showValid && (
        <div style={{ fontSize: 12, color: '#198754' }}>
          URL is valid
        </div>
      )}
      {hasError && !showError && (
        <div
          style={{ fontSize: 12, color: theme.CreateFormTheme.colors.error }}
        >
          {hasError}
        </div>
      )}
    </div>
  )
}

export default CustomInputURL

const InputContainer = styled.div <{ FixedPartWidth: string }>`
    border: 1px solid;
    border-radius: 5px;
    & .urlCon{

    }
    input {
        top: 1px;
        right: 1px;
        width: calc(100% - ${({ FixedPartWidth }) => FixedPartWidth});
        padding: 7px 12px;
        border: 0;
        border-left: 1px solid;
        outline: none;
        border-radius: 0px 5px 5px 0px;
        font-family: "Raleway", sans-serif;
    }
`;